import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, ImageOff, X } from "lucide-react";
import { formatDateTime } from "../../shared/utils/format";
import { useUserSection } from "./useUserSection";
import { LoadState, PlatformChips, StageBadge } from "./ui";
import type { PostDetail } from "./types";

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
    <div className="mt-0.5 text-sm text-gray-900 whitespace-pre-wrap break-words">{children}</div>
  </div>
);

function MediaViewer({ post }: Readonly<{ post: PostDetail }>) {
  const [index, setIndex] = useState(0);
  const { images, videoUrl } = post;

  if (videoUrl) {
    return (
      <video src={videoUrl} poster={images[0]} controls className="max-h-[70vh] w-full rounded-lg bg-black">
        <track kind="captions" />
      </video>
    );
  }
  if (images.length === 0) {
    return (
      <div className="flex h-72 flex-col items-center justify-center gap-2 rounded-lg bg-gray-100 text-sm text-gray-400">
        <ImageOff className="w-6 h-6" /> No media generated
      </div>
    );
  }

  const last = images.length - 1;
  return (
    <div className="space-y-3">
      <div className="relative">
        <img
          src={images[index]}
          alt={`Slide ${index + 1}`}
          className="max-h-[60vh] w-full rounded-lg bg-gray-100 object-contain"
        />
        {images.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous slide"
              disabled={index === 0}
              onClick={() => setIndex((i) => i - 1)}
              className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-1.5 shadow disabled:opacity-30"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              aria-label="Next slide"
              disabled={index === last}
              onClick={() => setIndex((i) => i + 1)}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-1.5 shadow disabled:opacity-30"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <span className="absolute bottom-2 right-2 rounded-full bg-black/60 px-2 py-0.5 text-xs text-white">
              {index + 1} / {images.length}
            </span>
          </>
        )}
      </div>
      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto">
          {images.map((url, i) => (
            <button
              key={url}
              type="button"
              aria-label={`Show slide ${i + 1}`}
              onClick={() => setIndex(i)}
              className={`shrink-0 rounded-md border-2 ${i === index ? "border-violet-600" : "border-transparent"}`}
            >
              <img src={url} alt="" loading="lazy" className="h-14 w-14 rounded object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function PostPreviewModal({
  userId,
  postId,
  onClose,
}: Readonly<{ userId: number; postId: number; onClose: () => void }>) {
  const { data: post, loading, error, reload } = useUserSection<PostDetail>(userId, `posts/${postId}`);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    globalThis.addEventListener("keydown", onKey);
    return () => globalThis.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button type="button" aria-label="Close preview" onClick={onClose} className="absolute inset-0 cursor-default bg-black/50" />
      <div className="relative max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-xl bg-white shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-gray-100 bg-white px-5 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <h2 className="truncate text-sm font-semibold text-gray-900">
              Post #{postId}
              {post?.businessName && <span className="font-normal text-gray-500"> · {post.businessName}</span>}
            </h2>
            {post && (
              <StageBadge
                stage={post.stage}
                detail={`record: ${post.status ?? "—"} · generation: ${post.generationStatus} · publishing: ${post.publishStatus}`}
              />
            )}
          </div>
          <button type="button" aria-label="Close" onClick={onClose} className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        {post ? (
          <div className="grid gap-6 p-5 md:grid-cols-2">
            <MediaViewer key={post.id} post={post} />
            <div className="space-y-4">
              {post.topic && <Field label="Topic">{post.topic}</Field>}
              {post.headline && <Field label="Headline">{post.headline}</Field>}
              {post.hook && <Field label="Hook">{post.hook}</Field>}
              <Field label="Caption">{post.caption || "—"}</Field>
              {post.hashtags && <Field label="Hashtags">{post.hashtags}</Field>}
              {post.cta && <Field label="CTA">{post.cta}</Field>}
              <Field label="Format">
                {post.contentType ?? post.media_type ?? "—"} · {(post.platforms ?? []).join(", ") || "no platforms"}
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Scheduled">{formatDateTime(post.scheduledAt)}</Field>
                <Field label="Published">{formatDateTime(post.publishedAt)}</Field>
                <Field label="Created">{formatDateTime(post.createdAt)}</Field>
                <Field label="Publish attempts">{post.publishAttempts}</Field>
              </div>
              {post.publishResults.length > 0 && (
                <Field label="Publishing">
                  <PlatformChips results={post.publishResults} />
                </Field>
              )}
              {post.stageReason && (
                <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{post.stageReason}</p>
              )}
            </div>
          </div>
        ) : (
          <LoadState loading={loading} error={error} onRetry={reload} />
        )}
      </div>
    </div>
  );
}
